import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../../services";
import type { IpcResult, Job, RunResponse } from "../../services/types";
import { elapsed, formatIST, useTick } from "../../lib/readAgo";
import { ConfirmBar } from "./ConfirmBar";

// A background run on the box: status polled every 2 seconds while it is
// queued or running, the log streamed by offset, elapsed time ticking. The
// job id lives in ?job= so a reload re-attaches.

export type JobId = number | string;

const ACTIVE = new Set<string>(["queued", "running"]);
export const isActiveStatus = (s?: string | null) => !!s && ACTIVE.has(s);

export function useJobParam(): [JobId | null, (id: JobId | null) => void] {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get("job");
  const id: JobId | null = raw ? (/^\d+$/.test(raw) ? Number(raw) : raw) : null;
  const set = useCallback(
    (next: JobId | null) => {
      const n = new URLSearchParams(sp);
      if (next == null) n.delete("job");
      else n.set("job", String(next));
      setSp(n, { replace: true });
    },
    [sp, setSp],
  );
  return [id, set];
}

/** State for a page's run buttons: the current job, any synchronous output, and the last error. */
export function useJobRunner() {
  const [jobId, setJobId] = useJobParam();
  const [out, setOut] = useState<{ title: string; result: RunResponse } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState("");

  async function run(title: string, fn: () => Promise<IpcResult<RunResponse>>): Promise<RunResponse | null> {
    setBusy(title);
    setErr(null);
    try {
      const r = await fn();
      if (!r.ok) {
        setErr(r.error);
        return null;
      }
      if (r.job) {
        setOut(null);
        setJobId(r.job.id);
      } else if (!r.requires_confirmation) {
        setOut({ title, result: r });
      }
      return r;
    } finally {
      setBusy("");
    }
  }

  return { jobId, setJobId, out, setOut, err, setErr, busy, run };
}

type RunnerState = ReturnType<typeof useJobRunner>;

/** Error, synchronous output and the job panel for one page's run section. */
export function RunResults({ runner, onFinish }: { runner: RunnerState; onFinish?: (job: Job) => void }) {
  return (
    <>
      {runner.err ? (
        <div className="note-neg"><p>{runner.err}</p></div>
      ) : null}
      {runner.out ? <RunOutput title={runner.out.title} result={runner.out.result} /> : null}
      <JobPanel jobId={runner.jobId} onFinish={onFinish} onClear={() => runner.setJobId(null)} />
    </>
  );
}

/** Output from a tool that still answers synchronously (the older dry run shape). */
export function RunOutput({ title, result }: { title: string; result: RunResponse }) {
  const hasProcess = typeof result.stdout === "string" || typeof result.stderr === "string";
  let text: string;
  if (hasProcess) {
    text = (result.stdout ?? "") + (result.stderr ? `\n\nstderr\n${result.stderr}` : "");
  } else {
    const { ok: _ok, ...rest } = result as RunResponse & { ok?: boolean };
    text = JSON.stringify(rest, null, 2);
  }
  return (
    <div className="job">
      <div className="job-head">
        <span>{title}</span>
        {typeof result.exit_code === "number" ? (
          <span>exit code<span className="v">{result.exit_code}</span></span>
        ) : null}
        {result.dry_run ? <span>dry run</span> : null}
      </div>
      <pre className="job-log">{text}</pre>
    </div>
  );
}

type Props = {
  jobId: JobId | null;
  onFinish?: (job: Job) => void;
  onClear?: () => void;
};

export function JobPanel({ jobId, onFinish, onClear }: Props) {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["job", jobId],
    enabled: jobId != null,
    queryFn: async () => {
      const r = await api.jobs.status(jobId!);
      if (!r.ok) throw new Error(r.error);
      return r.job;
    },
    refetchInterval: (q) => (isActiveStatus(q.state.data?.status) ? 2000 : false),
    staleTime: 0,
  });
  const job = statusQ.data;
  const active = isActiveStatus(job?.status);
  const now = useTick(active, 1000);

  // The log, appended by offset.
  const [text, setText] = useState("");
  const offset = useRef(0);
  const logRef = useRef<HTMLPreElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    setText("");
    offset.current = 0;
    stick.current = true;
  }, [jobId]);

  useEffect(() => {
    if (jobId == null) return;
    let stopped = false;
    const pull = async () => {
      const r = await api.jobs.log(jobId, offset.current, 65536);
      if (stopped || !r.ok) return;
      if (r.text) setText((t) => t + r.text);
      if (typeof r.next_offset === "number" && r.next_offset > offset.current) offset.current = r.next_offset;
    };
    void pull();
    if (!active) return;
    const id = setInterval(() => void pull(), 2000);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [jobId, active]);

  // Auto-scroll unless the reader scrolled up.
  useEffect(() => {
    const el = logRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [text]);

  // Tell the page once a run it was watching finishes.
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    const s = job?.status ?? null;
    if (job && lastStatus.current && isActiveStatus(lastStatus.current) && !isActiveStatus(s)) {
      onFinish?.(job);
    }
    lastStatus.current = s;
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (jobId == null) return null;

  if (statusQ.isError) {
    return (
      <div className="job">
        <div className="job-head"><span>Job<span className="v">{String(jobId)}</span></span></div>
        <div className="note-neg"><p>{(statusQ.error as Error).message}</p></div>
        {onClear ? (
          <div className="job-foot">
            <button type="button" className="btn sm quiet" onClick={onClear}>Close</button>
          </div>
        ) : null}
      </div>
    );
  }

  const summary = job?.summary;
  const summaryLines: [string, string][] =
    summary && typeof summary === "object"
      ? Object.entries(summary).map(([k, v]) => [k, typeof v === "string" ? v : JSON.stringify(v)])
      : typeof summary === "string" && summary
        ? [["summary", summary]]
        : [];

  return (
    <div className="job">
      <div className="job-head">
        <span>Job<span className="v">{String(jobId)}</span></span>
        {job ? (
          <>
            <span>kind<span className="v">{job.kind}</span></span>
            {job.key ? <span>key<span className="v">{job.key}</span></span> : null}
            <span>status<span className="v">{job.status}</span></span>
            {job.started_at ? <span>started<span className="v">{formatIST(job.started_at)}</span></span> : null}
            {job.started_at ? (
              <span>elapsed<span className="v">{elapsed(job.started_at, job.finished_at, now)}</span></span>
            ) : null}
            {typeof job.exit_code === "number" ? <span>exit code<span className="v">{job.exit_code}</span></span> : null}
          </>
        ) : (
          <span>Reading the job.</span>
        )}
      </div>
      <pre
        className="job-log"
        ref={logRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 12;
        }}
      >
        {text}
      </pre>
      {summaryLines.length > 0 ? (
        <div className="job-sum">
          {summaryLines.map(([k, v]) => (
            <div key={k}>{k}: <span>{v}</span></div>
          ))}
        </div>
      ) : null}
      {job?.error ? (
        <div className="note-neg"><p>{job.error}</p></div>
      ) : null}
      <div className="job-foot">
        {job && active ? (
          <ConfirmBar
            small
            label="Cancel this job"
            confirmLabel="Yes, stop it"
            note="Sends SIGTERM to the process group, then SIGKILL after 10 seconds."
            onConfirm={async () => {
              const r = await api.jobs.kill(jobId, true);
              if (r.ok) qc.setQueryData(["job", jobId], r.job);
              await statusQ.refetch();
            }}
          />
        ) : null}
        {job && !active && onClear ? (
          <button type="button" className="btn sm quiet" onClick={onClear}>Close</button>
        ) : null}
      </div>
    </div>
  );
}
